import React, { ReactElement, useState } from 'react';
import $i18n from '@/i18n';
import store from '@/store';
import { useAuth } from 'ice';
import { handleRedirect } from '@/utils';
import { LOGO as loginImg } from '@/utils/config';
import type { LoginParams } from '@/interfaces/user';
import { Form, Input, Message } from '@b-design/fusion';
import { login, fetchUserInfo, sendLoginCode } from '@/services/user';
import Button, { FormSubmit, promisifyValidate } from '@/components/Button';
import { Field } from '@alifd/next';
import './index.less';

interface LoginFormProps {
  children: ReactElement;
}

const LoginForm: React.FC<LoginFormProps> = (props) => {
  const field = Field.useField();
  const { init, validate, getError } = field;
  const [, userDispatcher] = store.useModel('user');
  const [, spaceRoleDispatcher] = store.useModel('spaceRole');
  const [isSendCode, setIsSendCode] = useState(false);
  const [time, setTime] = useState(60);
  const [, setAuth] = useAuth();

  async function updateUserInfo() {
    const userInfo = await fetchUserInfo();

    userDispatcher.updateCurrentUser(userInfo);
    return {
      _spaceId: userInfo?.defaultSpace?.spaceId,
      isAliyunPaymentUser: userInfo?.isAliyunPaymentUser,
      isDuibaUser: userInfo?.isDuibaUser,
      status: userInfo?.status,
      accountSrc: userInfo?.accountSrc,
    };
  }

  async function handleSubmit(values: LoginParams) {
    if (values.username && values.password && values.verifyCode) {
      try {
        const result = await login(values);
        if (result.success) {
          const obj = await updateUserInfo();
          Message.success($i18n.get({ id: 'aopf.pages.login.LoginSucceeded', dm: '登录成功！' }));
          setAuth({
            admin: result.userType === 'admin',
            user: result.userType === 'user',
          });
          const { _spaceId, isDuibaUser } = obj;
          handleRedirect(_spaceId, isDuibaUser, spaceRoleDispatcher);
        }
        // 如果失败去设置用户错误信息，显示提示信息
      } catch (error) {
        Message.error(
          $i18n.get({ id: 'aopf.pages.login.LoginFailedPleaseTryAgain', dm: '登录失败，请重试！' }),
        );
      }
    }
  }

  const handleSendCode = async () => {
    const [errors, values] = await promisifyValidate(field, ['username', 'password']);
    if (errors) return;
    const res = await sendLoginCode({
      loginType: 'pc',
      loginIdentifier: values.username,
    });
    if (!res) return;
    setIsSendCode(true);
    let times = time;
    let timer = setInterval(() => {
      if (times < 1) {
        setTime(60);
        setIsSendCode(false);
        clearInterval(timer);
      } else {
        times -= 1;
        setTime(times);
      }
    }, 1000);
  };

  const onValidate = async () => {
    const [errs, vals] = await promisifyValidate(field);
    if (errs) {
      return;
    }
    await handleSubmit(vals);
  };

  return (
    <div className="login-form">
      <div className="login-text">
        {$i18n.get({
          id: 'aopf.pages.login.form.title',
          dm: '登录',
        })}
      </div>
      <div className="login-icon">
        <img src={loginImg} style={{ width: 'auto', height: '100%' }} />
      </div>
      <Form style={{ maxWidth: '392px', margin: '0 auto', marginTop: '30px' }}>
        <Form.Item
          name="username"
          label={$i18n.get({
            id: 'aopf.pages.login.loginForm.label.account',
            dm: '账号',
          })}
          required
        >
          <Input
            placeholder="example@email.com"
            maxLength={64}
            showLimitHint
            {...init('username', {
              initValue: '',
              rules: {
                required: true,
                message: $i18n.get({
                  id: 'aopf.pages.login.loginForm.username.required',
                  dm: '请输入账号！',
                }),
              },
            })}
            onPressEnter={onValidate}
          />
          <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
            {getError('username')}
          </p>
        </Form.Item>

        <Form.Item
          name="password"
          label={$i18n.get({
            id: 'aopf.pages.login.loginForm.label.password',
            dm: '密码',
          })}
          required
        >
          <Input.Password
            placeholder={$i18n.get({
              id: 'aopf.pages.login.loginPasswordNotice',
              dm: '请输入您的密码',
            })}
            {...init('password', {
              initValue: '',
              rules: {
                required: true,
                message: $i18n.get({
                  id: 'aopf.pages.login.PleaseEnterThePassword',
                  dm: '请输入您的密码',
                }),
              },
            })}
            onPressEnter={onValidate}
          />
          {props.children}
          <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
            {getError('password')}
          </p>
        </Form.Item>

        <Form.Item
          name="verifyCode"
          label={$i18n.get({
            id: 'aopf.pages.login.verificationCode',
            dm: '验证码',
          })}
          required
        >
          <Input
            placeholder={
              isSendCode
                ? $i18n.get({
                    id: 'aopf.pages.login.PleaseEnterTheCode',
                    dm: '请输入您的验证码',
                  })
                : $i18n.get({
                    id: 'aopf.pages.login.loginEmailNotice',
                    dm: '发送验证码至您的邮箱账号',
                  })
            }
            innerAfter={
              <Button
                text
                type="normal"
                disabled={isSendCode}
                onClick={handleSendCode}
                style={{ padding: 0 }}
              >
                {isSendCode ? (
                  <div>
                    {$i18n.get(
                      {
                        id: 'aopf.pages.login.loginSendCodeTime',
                        dm: '发送验证码（{time}后获取）',
                      },
                      { time },
                    )}
                  </div>
                ) : (
                  <div className="send-code">
                    {$i18n.get({
                      id: 'aopf.pages.login.loginSendCode',
                      dm: '发送验证码',
                    })}
                  </div>
                )}
              </Button>
            }
            {...init('verifyCode', {
              initValue: '',
              rules: {
                required: true,
                message: $i18n.get({
                  id: 'aopf.pages.login.PleaseEnterTheCode',
                  dm: '请输入您的验证码',
                }),
              },
            })}
            onPressEnter={onValidate}
          />
          <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
            {getError('verifyCode')}
          </p>
        </Form.Item>

        <Form.Item name="agree">
          <FormSubmit
            type="primary"
            // onClick={async (values, errors) => {
            //   if (errors) return;
            //   await handleSubmit(values as LoginParams);
            // }}
            onClick={onValidate}
            validate
            style={{
              height: 40,
              width: 392,
              borderRadius: 4,
              fontWeight: 500,
              fontSize: 16,
              color: '#fff',
              letterSpacing: '0.15px',
              position: 'absolute',
              bottom: 0,
            }}
          >
            {$i18n.get({
              id: 'aopf.pages.login.loginForm.login',
              dm: '登录',
            })}
          </FormSubmit>
        </Form.Item>
      </Form>
    </div>
  );
};

export default LoginForm;
